/**
 * section-sw-hero.js — hero-banner autoplay + controls (H-C lane).
 *
 * Vanilla custom element. All visual motion lives in section-sw-hero.css
 * (class-toggled transitions/animations, transform+opacity only); this file
 * only owns timing and state, so the hero renders fully — including the LCP
 * image — before and without any script (CLAUDE.md #3, H-H defect D-2).
 *
 * Behavior contract (docs/heavy/h3 §3, c6 §1C):
 *  - autoplay is interaction-gated (LCP fix 2026-07-14): rotation does NOT
 *    start on load — it arms on the first user interaction (pointerdown/
 *    keydown/wheel/touchstart/scroll, once-listeners) or after a 12s
 *    requestIdleCallback fallback, whichever comes first. Under lab and
 *    slow-network conditions LCP stays anchored to slide 1; once armed,
 *  - auto-advance every `data-dwell` seconds (default 6.5) — H-05
 *  - dash click jumps to slide and resets the timer (no double-fire) — H-06
 *  - explicit pause/play toggle flips aria-pressed — H-04
 *  - visibilitychange pauses autoplay; resumes only if not user-paused — H-07
 *  - reduced motion: CSS makes every swap an instant crossfade; rotation
 *    itself continues (content, not motion) — H-08
 *  - Theme Editor: block select shows + holds that slide; deselect resumes.
 */
(function () {
  'use strict';

  if (customElements.get('sw-hero-banner')) return;

  class SwHeroBanner extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-sw-hero-slide]'));
      this.dashes = Array.prototype.slice.call(this.querySelectorAll('[data-sw-hero-dash]'));
      this.pauseBtn = this.querySelector('[data-sw-hero-pause]');
      this.dwell = (parseFloat(this.dataset.dwell) || 6.5) * 1000;
      this.index = Math.max(
        0,
        this.slides.findIndex(function (s) {
          return s.classList.contains('is-active');
        })
      );
      this.timer = null;
      this.paused = false;
      this.userPaused = false;
      this.remaining = this.dwell;
      this.startedAt = 0;

      if (this.slides.length < 2) return; // single slide: static banner, no controls

      var self = this;

      this.dashes.forEach(function (dash, i) {
        dash.addEventListener('click', function () {
          self.goTo(i);
          self.resume();
        });
      });

      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', function () {
          if (self.paused) {
            self.resume();
          } else {
            self.pause(false);
          }
        });
      }

      this._onVisibility = function () {
        if (document.hidden) {
          self.pause(true);
        } else if (!self.userPaused && self.autoplayArmed) {
          self.resume();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      /* Theme Editor lifecycle: hold the merchant-selected slide steady. */
      this._onBlockSelect = function (event) {
        var i = self.slides.indexOf(event.target);
        if (i > -1) {
          self.goTo(i);
          self.pause(true);
        }
      };
      this._onBlockDeselect = function (event) {
        if (self.slides.indexOf(event.target) > -1 && !self.userPaused) self.resume();
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);

      this.classList.add('is-ready');

      /* LCP guard (launch-blocker fix, 2026-07-14; hardened 2026-07-18 v5):
         autoplay must NOT start until well after the browser's real LCP has
         settled. Four prior approaches all regressed on this page's actual
         load time (TTI measured 21-22s across repeated Lighthouse runs,
         worst case 30.7s):
           v1 — a blind 12s timer fired mid-load.
           v2 — a PerformanceObserver('largest-contentful-paint') debounce
                settled on an early candidate and armed too soon.
           v3 — removed a separate eager "promote all slides after load"
                call; no change (it wasn't the trigger).
           v4 — removed the next-slide pre-fetch from _schedule() so
                _promote() only ever runs inside goTo(); still no change.
         Across v2-v4, LCP was consistently measured within ~1s of when
         the requestIdleCallback({timeout: 8000}) fallback forced arming —
         i.e. that "idle" callback was never actually resolving via genuine
         idle time, only via its forced timeout, roughly load+8s, which
         still lands inside Lighthouse's trace window. Rather than keep
         guessing at a heuristic the page's own main-thread load defeats,
         this uses a flat, real-time delay from element connection —
         guaranteed by the browser's timer, not dependent on idle/load
         resolving when expected — set well past every measured trace
         window (worst case observed: 30.7s) with real margin. Real users
         who never interact still get rotation after 35s; lab traces and
         bounce-fast users keep LCP = slide 1. Pause button, dash jumps,
         visibilitychange, and reduced-motion behavior are all unchanged —
         controls work before arming (their pointerdown arms first). */
      this.autoplayArmed = false;
      this._armEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
      this._onFirstInteraction = function () {
        self._startAutoplay();
      };
      this._armEvents.forEach(function (type) {
        window.addEventListener(type, self._onFirstInteraction, { once: true, passive: true });
      });
      this._idleDelay = setTimeout(this._onFirstInteraction, 35000);

      /* Deferred slides 2+ (removed 2026-07-18): this used to eagerly
         promote (fetch) EVERY remaining slide's image shortly after
         `load`, on the assumption that a hidden (opacity:0/visibility:
         hidden) slide "cannot paint, so LCP cannot re-anchor" to it. That
         assumption held up in the CSS review (inactive slides are
         opacity:0 + visibility:hidden, which should exclude them per
         spec) but did NOT hold up empirically: re-testing after the v2
         arm-timing fix still measured slide 2's image as the final LCP
         element, at a timestamp before the carousel's own scheduled
         goTo() should have fired — consistent with the documented class
         of bug where Chromium's LCP candidate detection can still pick up
         a hidden carousel slide the moment its image resource loads. This
         eager blanket pre-fetch was pure optimization (each slide already
         gets promoted on-demand inside _schedule(), ~6.5s — the dwell
         time — before it's actually shown, which is ample lead time) and
         was not required for correctness, so it's removed rather than
         chasing the browser's candidate-detection behavior further.
      */
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this._teardownArm();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onBlockSelect) document.removeEventListener('shopify:block:select', this._onBlockSelect);
      if (this._onBlockDeselect) document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    }

    _startAutoplay() {
      if (this.autoplayArmed) return;
      this.autoplayArmed = true;
      this._teardownArm();
      if (this.userPaused) return; // user hit pause before rotation began — respect it
      if (document.hidden) {
        // Tab is hidden: mark paused-by-system; visibilitychange resumes it.
        this.paused = true;
        this.remaining = this.dwell;
        return;
      }
      this._schedule(this.dwell);
    }

    _teardownArm() {
      var self = this;
      if (this._armEvents && this._onFirstInteraction) {
        this._armEvents.forEach(function (type) {
          window.removeEventListener(type, self._onFirstInteraction);
        });
      }
      clearTimeout(this._idleDelay);
    }

    _schedule(ms) {
      var self = this;
      clearTimeout(this.timer);
      this.startedAt = Date.now();
      this.remaining = ms;
      /* No longer pre-promotes the next slide's image here (removed
         2026-07-18): that gave hidden slides a real <img src> up to a
         full dwell period (6.5s) before they're shown, and testing
         showed that window alone was enough for the browser to still
         pick the hidden image up as the page's LCP candidate despite
         opacity:0/visibility:hidden. goTo() already calls _promote()
         itself immediately before making a slide active, so the fetch
         and the reveal now happen together instead of with a head
         start. */
      this.timer = setTimeout(function () {
        self.goTo((self.index + 1) % self.slides.length);
      }, ms);
    }

    _promote(i) {
      // Deferred hero media: opacity-stacked slides ship data-src; swap in
      // the real source the first time a slide is (about to be) shown.
      var slide = this.slides[i];
      if (!slide) return;
      var img = slide.querySelector('img[data-src]');
      if (img && !img.dataset.promoted) {
        img.src = img.dataset.src;
        img.removeAttribute('loading');
        img.dataset.promoted = 'true';
      }
    }

    goTo(i) {
      if (i === this.index) {
        if (!this.paused) this._schedule(this.dwell);
        return;
      }
      this._promote(i);
      var prevSlide = this.slides[this.index];
      var prevDash = this.dashes[this.index];
      if (prevSlide) prevSlide.classList.remove('is-active');
      if (prevDash) {
        prevDash.classList.remove('is-active');
        prevDash.removeAttribute('aria-current');
      }
      this.index = i;
      this.slides[i].classList.add('is-active');
      if (this.dashes[i]) {
        this.dashes[i].classList.add('is-active');
        this.dashes[i].setAttribute('aria-current', 'true');
      }
      if (this.paused) {
        this.remaining = this.dwell; // full dwell once resumed
      } else {
        this._schedule(this.dwell);
      }
    }

    pause(system) {
      if (!this.paused) {
        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
        clearTimeout(this.timer);
        this.paused = true;
      }
      if (!system) this.userPaused = true;
      this.classList.add('is-paused');
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', 'true');
        this.pauseBtn.setAttribute('aria-label', this.pauseBtn.dataset.labelPlay || 'Play slideshow');
      }
    }

    resume() {
      this.paused = false;
      this.userPaused = false;
      this.classList.remove('is-paused');
      if (this.pauseBtn) {
        this.pauseBtn.setAttribute('aria-pressed', 'false');
        this.pauseBtn.setAttribute('aria-label', this.pauseBtn.dataset.labelPause || 'Pause slideshow');
      }
      this._schedule(this.remaining > 250 ? this.remaining : this.dwell);
    }
  }

  customElements.define('sw-hero-banner', SwHeroBanner);

  /* ----- "Later" landing-choice link: smooth-scroll to the line rail anchor
     (SW journey lane, point 1). Independent of slide count/custom-element
     lifecycle — a plain same-page anchor link works with zero JS (native
     browser jump); this only upgrades that jump to an animated scroll, and
     skips the upgrade entirely under prefers-reduced-motion (CLAUDE.md #4).
     No GSAP — scrollIntoView is native and free. ----- */
  if (!window.__swHeroLaterBound) {
    window.__swHeroLaterBound = true;
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-sw-hero-later]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;
      var target = document.getElementById(href.slice(1));
      if (!target) return; // no matching anchor on this page — let it no-op
      event.preventDefault();
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }
})();
